/**
 * Created: 2026-09-22
 * Refactor: Split CoursesPage into a directory
 *
 * Page for listing and creating courses for a site. Only super_admins of
 * the site may create a course.
 */

import { gql } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import RefreshIcon from '@mui/icons-material/Refresh'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { Alert, Box, Button, IconButton, Tooltip, Typography } from '@mui/material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useState } from 'react'
import {
  CoursesDocument,
  type CoursesQuery,
  ListingStatus,
  SiteRole,
  type SportCategory,
  UpdateCourseDocument,
} from '../../gql/graphql'
import { useRole } from '../../hooks'
import { SPORT_LABEL } from '../../lib/domainLabels'
import CourseDialog from './CourseDialog'

// Created: 2026-09-22 | Initial commit: Add courses management page
gql`
  query Courses($siteId: UUID) {
    courses(siteId: $siteId) {
      id
      name
      description
      sport
      hours
      price
      maxStudents
      perUserLimit
      coverImages
      status
      createTime
    }
  }

  mutation CreateCourse($courseInput: CreateCourseInput!) {
    createCourse(courseInput: $courseInput) {
      id
      name
      description
      sport
      hours
      price
      maxStudents
      perUserLimit
      coverImages
      status
      createTime
    }
  }

  mutation UpdateCourse($courseInput: UpdateCourseInput!) {
    updateCourse(courseInput: $courseInput) {
      id
      name
      description
      sport
      hours
      price
      maxStudents
      perUserLimit
      coverImages
      status
      createTime
    }
  }
`

type Course = NonNullable<CoursesQuery['courses']>[number]

function buildColumns(
  isSuperAdmin: boolean,
  onToggleStatus: (course: Course) => void,
  onEdit: (course: Course) => void,
): GridColDef<Course>[] {
  const columns: GridColDef<Course>[] = [
    {
      field: 'sport',
      headerName: '运动',
      width: 90,
      headerAlign: 'center',
      align: 'center',
      valueFormatter: (value: SportCategory) => SPORT_LABEL[value] ?? value,
    },
    { field: 'name', headerName: '名称', width: 200, headerAlign: 'center' },
    {
      field: 'hours',
      headerName: '课时数',
      width: 90,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'price',
      headerName: '售价',
      width: 100,
      headerAlign: 'center',
      align: 'center',
      valueFormatter: (value: number) => `¥${value}`,
    },
    {
      field: 'maxStudents',
      headerName: '人数',
      width: 80,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'perUserLimit',
      headerName: '限购',
      width: 80,
      headerAlign: 'center',
      align: 'center',
      valueFormatter: (value: number | null) => (value ? String(value) : '不限'),
    },
    {
      field: 'status',
      headerName: '状态',
      width: 100,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const isOnsale = params.value === ListingStatus.Listed
        return (
          <Box
            component="span"
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              fontSize: '0.875rem',
              fontWeight: 500,
              backgroundColor: isOnsale ? '#dcfce7' : '#f3f4f6',
              color: isOnsale ? '#16a34a' : '#6b7280',
            }}
          >
            {isOnsale ? '在售' : '下架'}
          </Box>
        )
      },
    },
  ]

  if (isSuperAdmin) {
    columns.push({
      field: 'actions',
      headerName: '操作',
      width: 120,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params) => {
        const isOnsale = params.row.status === ListingStatus.Listed
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              height: '100%',
            }}
          >
            <Tooltip title={isOnsale ? '下架' : '上架'}>
              <IconButton size="small" onClick={() => onToggleStatus(params.row)}>
                {isOnsale ? (
                  <VisibilityOffIcon fontSize="small" />
                ) : (
                  <VisibilityIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title="编辑">
              <IconButton size="small" onClick={() => onEdit(params.row)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )
      },
    })
  }

  return columns
}

export default function CoursesPage() {
  const { currentSite } = useRole()
  const { data, loading, error, refetch } = useQuery(CoursesDocument, {
    variables: { siteId: currentSite?.id as string },
    skip: !currentSite,
    fetchPolicy: 'network-only',
  })

  const [updateCourse] = useMutation(UpdateCourseDocument)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [editingCourse, setEditingCourse] = useState<Course | null>(null)

  const handleSaved = async () => {
    await refetch()
    setShowCreateDialog(false)
    setEditingCourse(null)
  }

  const handleEdit = (course: Course) => {
    setEditingCourse(course)
  }

  const handleToggleStatus = async (course: Course) => {
    const newStatus =
      course.status === ListingStatus.Listed
        ? ListingStatus.Delisted
        : ListingStatus.Listed

    try {
      await updateCourse({
        variables: {
          courseInput: {
            id: course.id,
            status: newStatus,
          },
        },
      })
      await refetch()
    } catch (err) {
      console.error('Failed to toggle course status:', err)
    }
  }

  const isSuperAdmin = currentSite?.roles.includes(SiteRole.SuperAdmin) ?? false
  const courses = [...(data?.courses ?? [])].sort((a, b) => {
    // Sort by sport first
    if (a.sport !== b.sport) {
      return a.sport.localeCompare(b.sport)
    }
    // Then by price
    return Number(a.price) - Number(b.price)
  })
  const columns = buildColumns(isSuperAdmin, handleToggleStatus, handleEdit)

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>
        课程管理
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
        <Button size="small" startIcon={<RefreshIcon />} onClick={() => refetch()}>
          刷新
        </Button>
        {isSuperAdmin && (
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowCreateDialog(true)}
          >
            新建课程
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}

      <DataGrid
        rows={courses}
        columns={columns}
        loading={loading}
        autoHeight
        hideFooter
        disableColumnMenu
        disableRowSelectionOnClick
      />

      {showCreateDialog && currentSite && (
        <CourseDialog
          siteId={currentSite.id}
          onClose={() => setShowCreateDialog(false)}
          onSaved={handleSaved}
        />
      )}

      {editingCourse && currentSite && (
        <CourseDialog
          siteId={currentSite.id}
          course={editingCourse}
          onClose={() => setEditingCourse(null)}
          onSaved={handleSaved}
        />
      )}
    </Box>
  )
}
